import { getModelApiDefaults } from "@/client/config/modelApiDefaults";
import type { TaskType } from "@/client/adapters/types";
import type { SchemaGenerationPayload } from "@/client/types/generation";
import { extractFirstUrlFromUpload, extractUrlsFromUpload, extractFirstFileFromUpload } from "@/features/models/shared/formTransforms";
import {
  extractFirstFile,
  extractAllFiles,
  extractFirstFileOrUrl,
} from "@/features/canvas/services/mediaDataContract";
import { evaluateCondition } from "./helpers";
import type {
  SchemaBuildContext,
  SchemaBuiltinTransform,
  SchemaComputedValue,
  SchemaCondition,
  SchemaFieldKey,
  SchemaPayloadDefinition,
  SchemaTimelineSegment,
  SchemaValueExpression,
  SchemaValueTransform,
  SchemaValueTransformFactory,
} from "./types";

type PathSegment = string | number;

interface ValueEvaluationState<FieldKey extends string = SchemaFieldKey> {
  context: SchemaBuildContext<FieldKey>;
  computeCache: Map<string | SchemaComputedValue<FieldKey>, Promise<unknown>>;
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toNumberValue = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const toStringArrayValue = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => toTrimmedString(item))
      .filter((item): item is string => Boolean(item));
  }
  if (typeof value === "string") {
    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }
  return [];
};

const normalizeShotListValue = (value: unknown): SchemaTimelineSegment[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  let cursor = 0;
  return value.reduce<SchemaTimelineSegment[]>((segments, item, index) => {
    if (!isObjectLike(item)) {
      return segments;
    }
    const record = item as Record<string, unknown>;
    const providedIndex = toNumberValue(record.index);
    const start = toNumberValue(record.startAtSeconds ?? record.startAt ?? record.start);
    const end = toNumberValue(record.endAtSeconds ?? record.endAt ?? record.end);
    let duration = toNumberValue(record.durationSeconds ?? record.duration ?? record.seconds ?? record.length);
    const startAt = start ?? cursor;
    let endAt = end;

    if (typeof duration === "number" && duration < 0) {
      duration = Math.abs(duration);
    }

    if (duration === undefined && endAt !== undefined) {
      duration = Math.max(0, endAt - startAt);
    }

    if (duration !== undefined && endAt === undefined) {
      endAt = startAt + duration;
    }

    if (duration !== undefined && duration === 0) {
      duration = undefined;
    }

    if (endAt !== undefined && duration === undefined && start !== undefined) {
      duration = Math.max(0, endAt - startAt);
    }

    if (endAt !== undefined) {
      cursor = endAt;
    } else if (duration !== undefined) {
      cursor = startAt + duration;
    }

    const beats = toStringArrayValue(record.beats ?? record.storyBeats ?? record.timeline ?? record.notes);
    const tags = toStringArrayValue(record.tags);

    const segment: SchemaTimelineSegment = {
      id: toTrimmedString(record.id ?? record.key ?? record.slug),
      index: providedIndex ?? index,
      title: toTrimmedString(record.title ?? record.name ?? record.label),
      summary: toTrimmedString(record.summary ?? record.logline),
      description: toTrimmedString(record.description ?? record.prompt ?? record.text),
      beats: beats.length > 0 ? beats : undefined,
      durationSeconds: duration,
      cameraMotion: toTrimmedString(record.cameraMotion ?? record.camera ?? record.motion),
      audioCue: toTrimmedString(record.audioCue ?? record.audio ?? record.soundtrack),
      referenceUrl: toTrimmedString(record.referenceUrl ?? record.reference ?? record.referenceImage ?? record.assetUrl),
      tags: tags.length > 0 ? tags : undefined,
      transition: toTrimmedString(record.transition),
      startAtSeconds: startAt,
      endAtSeconds: endAt,
      metadata: isObjectLike(record.metadata) ? (record.metadata as Record<string, unknown>) : undefined,
    };

    if (!segment.description) {
      if (segment.summary) {
        segment.description = segment.summary;
      } else if (segment.beats) {
        segment.description = segment.beats.join("; ");
      }
    }

    segments.push(segment);
    return segments;
  }, []);
};

const timelineToNarrative = (value: unknown): string | undefined => {
  const segments = normalizeShotListValue(value);
  if (!segments.length) {
    return undefined;
  }
  const lines = segments
    .map((segment, idx) => {
      const order = (segment.index ?? idx) + 1;
      const parts: string[] = [`${order}.`];
      if (typeof segment.durationSeconds === "number" && segment.durationSeconds > 0) {
        parts.push(`[${Math.round(segment.durationSeconds)}s]`);
      }
      if (segment.title) {
        parts.push(segment.title);
      }
      const descriptors: string[] = [];
      if (segment.description) {
        descriptors.push(segment.description);
      }
      if (segment.cameraMotion) {
        descriptors.push(`Camera: ${segment.cameraMotion}`);
      }
      if (segment.beats && segment.beats.length > 0) {
        descriptors.push(`Beats: ${segment.beats.join(" / ")}`);
      }
      if (segment.audioCue) {
        descriptors.push(`Audio: ${segment.audioCue}`);
      }
      if (segment.transition) {
        descriptors.push(`Transition: ${segment.transition}`);
      }
      const descriptorText = descriptors.join(" | ");
      if (descriptorText) {
        parts.push("—", descriptorText);
      }
      return parts.join(" ").replace(/\s+/g, " ").trim();
    })
    .filter((line) => line.length > 0);

  if (!lines.length) {
    return undefined;
  }
  return lines.join("\n");
};

const splitMultilineText = (value: unknown): string[] => {
  if (typeof value !== "string") {
    return [];
  }
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
};

const compactArrayValues = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item) => {
    if (item === null || item === undefined) {
      return false;
    }
    if (typeof item === "string") {
      return item.trim().length > 0;
    }
    return true;
  });
};

const BUILTIN_TRANSFORMS: Record<SchemaBuiltinTransform, SchemaValueTransformFactory<SchemaFieldKey>> = {
  trim: async (value) => (typeof value === "string" ? value.trim() : value),
  toLowerCase: async (value) => (typeof value === "string" ? value.toLowerCase() : value),
  toUpperCase: async (value) => (typeof value === "string" ? value.toUpperCase() : value),
  toNumber: async (value) => {
    if (value === undefined || value === null || value === "") return undefined;
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  },
  ensureArray: async (value) => {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null) return [];
    return [value];
  },
  first: async (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : undefined;
    }
    return value;
  },
  extractUrls: async (value) => extractUrlsFromUpload(value),
  extractFirstUrl: async (value) => extractFirstUrlFromUpload(value),
  extractFirstFile: async (value) => extractFirstFileFromUpload(value),
  splitLines: async (value) => splitMultilineText(value),
  compactArray: async (value) => compactArrayValues(value),
  shotListToTimeline: async (value) => normalizeShotListValue(value),
  timelineToScript: async (value) => timelineToNarrative(value),
  firstUploadFile: async (value) => extractFirstFile(value),
  firstUploadFileOrUrl: async (value) => extractFirstFileOrUrl(value),
  allUploadFiles: async (value) => extractAllFiles(value),
};

const isObjectLike = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isFormData = (value: unknown): value is FormData =>
  typeof FormData !== "undefined" && value instanceof FormData;

const parsePath = (path: string): PathSegment[] => {
  if (!path) return [];
  const segments: PathSegment[] = [];
  const pattern = /([^.[\]]+)|(\[(\d+)\])/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(path)) !== null) {
    if (match[1]) {
      segments.push(match[1]);
    } else if (match[3]) {
      segments.push(Number.parseInt(match[3], 10));
    }
  }
  return segments;
};

const createDefaultBody = (format?: SchemaPayloadDefinition["format"]): Record<string, unknown> | FormData => {
  if (format === "multipart") {
    if (typeof FormData === "undefined") {
      throw new Error("当前环境不支持 FormData，无法构建 multipart 请求");
    }
    return new FormData();
  }
  return {};
};

const buildFormDataKey = (segments: PathSegment[]): string =>
  segments
    .map((segment, index) => {
      if (typeof segment === "number") {
        return `[${segment}]`;
      }
      return index === 0 ? segment : `.${segment}`;
    })
    .join("")
    .replace(/^\./, "");

const appendFormDataValue = (formData: FormData, key: string, value: unknown): void => {
  if (value === undefined || value === null) {
    return;
  }
  const appendEntry = (entry: unknown) => {
    if (entry === undefined || entry === null) return;
    const isFile = typeof File !== "undefined" && entry instanceof File;
    const isBlob = typeof Blob !== "undefined" && entry instanceof Blob;
    if (isFile) {
      formData.append(key, entry, entry.name);
      return;
    }
    if (isBlob) {
      formData.append(key, entry);
      return;
    }
    if (Array.isArray(entry)) {
      entry.forEach((item) => appendEntry(item));
      return;
    }
    if (typeof entry === "object") {
      formData.append(key, JSON.stringify(entry));
      return;
    }
    formData.append(key, String(entry));
  };

  appendEntry(value);
};

const ensureChildContainer = (
  parent: Record<string, unknown> | unknown[],
  segment: PathSegment,
  nextSegment: PathSegment | undefined
): Record<string, unknown> | unknown[] => {
  if (typeof segment === "number") {
    if (!Array.isArray(parent)) {
      throw new Error("路径定义与数据结构不匹配，期望数组却找到非数组节点");
    }
    if (parent[segment] === undefined) {
      parent[segment] = typeof nextSegment === "number" ? [] : {};
    } else if (typeof nextSegment === "number" && !Array.isArray(parent[segment])) {
      parent[segment] = [];
    } else if (typeof nextSegment === "string" && !isObjectLike(parent[segment])) {
      parent[segment] = {};
    }
    return parent[segment] as Record<string, unknown> | unknown[];
  }

  const expectsArray = typeof nextSegment === "number";
  const current = (parent as Record<string, unknown>)[segment];
  if (current === undefined || current === null) {
    (parent as Record<string, unknown>)[segment] = expectsArray ? [] : {};
    return (parent as Record<string, unknown>)[segment] as Record<string, unknown> | unknown[];
  }
  if (expectsArray && !Array.isArray(current)) {
    (parent as Record<string, unknown>)[segment] = [];
    return (parent as Record<string, unknown>)[segment] as unknown[];
  }
  if (!expectsArray && !isObjectLike(current)) {
    (parent as Record<string, unknown>)[segment] = {};
    return (parent as Record<string, unknown>)[segment] as Record<string, unknown>;
  }
  return current as Record<string, unknown> | unknown[];
};

const assignValue = (
  payload: SchemaGenerationPayload,
  path: string,
  value: unknown,
  format?: SchemaPayloadDefinition["format"]
): void => {
  if (value === undefined) return;
  const segments = parsePath(path);
  if (segments.length === 0) return;

  // FormData 专用写入
  if (segments[0] === "body" && segments.length > 1) {
    if (!payload.body) {
      payload.body = createDefaultBody(format);
    }
    if (isFormData(payload.body)) {
      const key = buildFormDataKey(segments.slice(1));
      appendFormDataValue(payload.body, key, value);
      return;
    }
  }

  let target: Record<string, unknown> | unknown[] = payload as Record<string, unknown>;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    const next = segments[i + 1];

    if (segment === "body" && payload.body === undefined) {
      payload.body = createDefaultBody(format);
    }
    if (segment === "body" && payload.body !== undefined) {
      target = payload.body as Record<string, unknown>;
      continue;
    }
    if (segment === "api") {
      if (!payload.api) {
        payload.api = {};
      }
      target = payload.api as Record<string, unknown>;
      continue;
    }
    if (segment === "meta") {
      if (!payload.meta) {
        payload.meta = {};
      }
      target = payload.meta as Record<string, unknown>;
      continue;
    }

    target = ensureChildContainer(target, segment, next);
  }

  const last = segments[segments.length - 1];
  if (last === "body") {
    payload.body = value as SchemaGenerationPayload["body"];
    return;
  }
  if (last === "api") {
    payload.api = value as SchemaGenerationPayload["api"];
    return;
  }
  if (last === "meta") {
    payload.meta = value as SchemaGenerationPayload["meta"];
    return;
  }

  if (typeof last === "number") {
    if (!Array.isArray(target)) {
      throw new Error(`路径 ${path} 无法写入索引 ${last}`);
    }
    target[last] = value;
    return;
  }

  (target as Record<string, unknown>)[last] = value;
};

const applyTransforms = async <FieldKey extends string>(
  value: unknown,
  transforms: SchemaValueTransform<FieldKey>[] | undefined,
  state: ValueEvaluationState<FieldKey>,
  options: { field?: FieldKey; targetPath?: string }
): Promise<unknown> => {
  if (!transforms || transforms.length === 0) {
    return value;
  }
  let current = value;
  for (const transform of transforms) {
    const handler: SchemaValueTransformFactory<FieldKey> =
      typeof transform === "string"
        ? (((): SchemaValueTransformFactory<FieldKey> => {
            const builtin = BUILTIN_TRANSFORMS[transform as SchemaBuiltinTransform];
            if (!builtin) {
              throw new Error(`未定义的内置 transform: ${transform}`);
            }
            return builtin as unknown as SchemaValueTransformFactory<FieldKey>;
          })())
        : transform;
    current = await handler(current, {
      field: options.field,
      targetPath: options.targetPath,
      values: state.context.values,
      schema: state.context.schema,
      modelKey: state.context.modelKey,
      runtime: state.context.runtime,
    });
  }
  return current;
};

const evaluateValueExpression = async <FieldKey extends string>(
  expression: SchemaValueExpression<FieldKey> | undefined,
  state: ValueEvaluationState<FieldKey>,
  options: { targetPath?: string } = {}
): Promise<unknown> => {
  if (!expression) {
    return undefined;
  }

  switch (expression.kind) {
    case "literal":
      return expression.value;
    case "field": {
      const raw = state.context.values[expression.field];
      const transformed = await applyTransforms(raw, expression.transforms, state, {
        field: expression.field,
        targetPath: options.targetPath,
      });
      if (transformed === undefined && expression.fallback) {
        return evaluateValueExpression(expression.fallback, state, options);
      }
      return transformed;
    }
    case "compute": {
      const cacheKey = expression.cacheKey ?? expression;
      if (!state.computeCache.has(cacheKey)) {
        const promise = Promise.resolve(expression.run(state.context));
        state.computeCache.set(cacheKey, promise);
      }
      return state.computeCache.get(cacheKey);
    }
    case "when": {
      const passes = await evaluateCondition(expression.condition as SchemaCondition<FieldKey>, {
        schema: state.context.schema,
        values: state.context.values,
        modelKey: state.context.modelKey,
      });
      if (passes) {
        return evaluateValueExpression(expression.truthy, state, options);
      }
      return evaluateValueExpression(expression.falsy, state, options);
    }
    default:
      return undefined;
  }
};

const ensureApiDefaults = (payload: SchemaGenerationPayload): void => {
  if (!payload.api) return;
  const hasFormDataBody = isFormData(payload.body);
  if (payload.api.contentType === undefined) {
    payload.api.contentType = hasFormDataBody ? null : "application/json";
  }
};

export const buildSchemaPayload = async <FieldKey extends string = SchemaFieldKey>(
  context: SchemaBuildContext<FieldKey>
): Promise<SchemaGenerationPayload> => {
  const runtime = context.runtime ?? {};
  const buildContext: SchemaBuildContext<FieldKey> = {
    ...context,
    runtime,
  };

  const { schema } = buildContext;
  const { payload: payloadDefinition } = schema;

  if (!payloadDefinition) {
    throw new Error("schema.payload 未定义，无法构建请求");
  }

  const payload: SchemaGenerationPayload = {};
  const apiDefaults = getModelApiDefaults(buildContext.modelKey);
  if (apiDefaults) {
    payload.api = { ...apiDefaults };
  }

  if (payloadDefinition.hooks?.beforeBuild) {
    await payloadDefinition.hooks.beforeBuild(buildContext);
  }

  const state: ValueEvaluationState<FieldKey> = {
    context: buildContext,
    computeCache: new Map(),
  };

  const entries = Object.entries(payloadDefinition.map ?? {});
  for (const [path, expression] of entries) {
    const result = await evaluateValueExpression(expression, state, { targetPath: path });
    assignValue(payload, path, result, payloadDefinition.format);
  }

  if (payloadDefinition.taskType) {
    if (!payload.api) {
      payload.api = { taskType: payloadDefinition.taskType as TaskType | undefined };
    } else if (payload.api.taskType === undefined) {
      payload.api.taskType = payloadDefinition.taskType as TaskType | undefined;
    }
  }

  ensureApiDefaults(payload);

  if (payloadDefinition.hooks?.afterBuild) {
    const maybeNext = await payloadDefinition.hooks.afterBuild(payload, buildContext);
    if (maybeNext !== undefined) {
      return maybeNext;
    }
  }

  return payload;
};
